import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export interface ImageCarouselItem {
  src: string;
  alt: string;
  /** Internal path ("/product/abc") or absolute URL. Slide is not clickable when empty */
  url?: string | null;
}

export interface ImageCarouselProps {
  items: ImageCarouselItem[];
  /** Width / height of each slide. Defaults to a wide ~4:1 banner */
  aspectRatio?: string;
  autoPlaySpeed?: number;
  showArrows?: boolean;
  showIndicators?: boolean;
  indicatorVariant?: 'light' | 'primary';
  className?: string;
  style?: React.CSSProperties;
  pauseOnHover?: boolean;
}

const styles = {
  container: (aspectRatio: string) => ({
    width: '100%',
    aspectRatio,
    overflow: 'hidden',
    position: 'relative' as const,
    userSelect: 'none' as const,
  }),
  slide: {
    flex: '0 0 100%',
    width: '100%',
    height: '100%',
    position: 'relative' as const,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
    pointerEvents: 'none' as const,
  },
  arrow: {
    width: 36,
    height: 36,
    transition: 'transform 0.15s ease, background-color 0.15s ease',
  },
  dot: (active: boolean) => ({
    width: active ? 22 : 8,
    height: 8,
    borderRadius: 4,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
  }),
};

const isExternal = (url: string) => /^https?:\/\//i.test(url);

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  items,
  aspectRatio = '4 / 1',
  autoPlaySpeed = 3500,
  showArrows = false,
  showIndicators = true,
  indicatorVariant = 'light',
  className = '',
  style,
  pauseOnHover = false,
}) => {
  const total = items?.length || 0;
  const hasMultiple = total > 1;

  // Infinite track: [cloneOfLast, ...items, cloneOfFirst]
  const slides = useMemo(() => {
    if (!hasMultiple) return items || [];
    return [items[total - 1], ...items, items[0]];
  }, [items, hasMultiple, total]);

  // displayIndex: 1 maps to real slide 0
  const [displayIndex, setDisplayIndex] = useState(hasMultiple ? 1 : 0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Touch and drag states
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef<number | null>(null);
  const currentDragRef = useRef<number>(0);
  const hasDraggedRef = useRef<boolean>(false);

  // Real active index (0 to total - 1)
  const activeDot = useMemo(() => {
    if (!hasMultiple) return 0;
    return (displayIndex - 1 + total) % total;
  }, [displayIndex, total, hasMultiple]);

  // Navigate to next slide with smooth scroll
  const nextSlide = useCallback(() => {
    if (!hasMultiple) return;
    setIsTransitioning(true);
    setDisplayIndex((prev) => prev + 1);
  }, [hasMultiple]);

  // Navigate to previous slide with smooth scroll
  const prevSlide = useCallback(() => {
    if (!hasMultiple) return;
    setIsTransitioning(true);
    setDisplayIndex((prev) => prev - 1);
  }, [hasMultiple]);

  // Direct dot click navigation
  const goToSlide = (targetRealIndex: number) => {
    if (!hasMultiple) return;
    setIsTransitioning(true);
    setDisplayIndex(targetRealIndex + 1);
  };

  // Autoplay timer
  useEffect(() => {
    if (!hasMultiple || isPaused || isDragging) return;

    const timer = setInterval(() => {
      nextSlide();
    }, autoPlaySpeed);

    return () => clearInterval(timer);
  }, [hasMultiple, isPaused, isDragging, autoPlaySpeed, nextSlide]);

  // Handle transition end for infinite looping
  const handleTransitionEnd = () => {
    if (!hasMultiple) return;

    if (displayIndex >= slides.length - 1) {
      // Reached cloned first slide at end -> instantly jump to real first slide (index 1)
      setIsTransitioning(false);
      setDisplayIndex(1);
    } else if (displayIndex <= 0) {
      // Reached cloned last slide at start -> instantly jump to real last slide (index total)
      setIsTransitioning(false);
      setDisplayIndex(total);
    }
  };

  // Re-enable transition on subsequent frame after an instant jump
  useEffect(() => {
    if (!isTransitioning && hasMultiple) {
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [isTransitioning, hasMultiple]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!hasMultiple) return;
    startXRef.current = e.touches[0].clientX;
    currentDragRef.current = 0;
    hasDraggedRef.current = false;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!hasMultiple || startXRef.current === null) return;
    const diff = e.touches[0].clientX - startXRef.current;
    currentDragRef.current = diff;
    if (Math.abs(diff) > 5) {
      hasDraggedRef.current = true;
    }
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!hasMultiple) return;
    const diff = currentDragRef.current;
    setIsDragging(false);
    setDragOffset(0);
    startXRef.current = null;

    if (diff < -50) {
      nextSlide();
    } else if (diff > 50) {
      prevSlide();
    }
  };

  // Mouse drag handlers for desktop smooth dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!hasMultiple || e.button !== 0) return;
    startXRef.current = e.clientX;
    currentDragRef.current = 0;
    hasDraggedRef.current = false;
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!hasMultiple || !isDragging || startXRef.current === null) return;
    const diff = e.clientX - startXRef.current;
    currentDragRef.current = diff;
    if (Math.abs(diff) > 5) {
      hasDraggedRef.current = true;
    }
    setDragOffset(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const diff = currentDragRef.current;
    setIsDragging(false);
    setDragOffset(0);
    startXRef.current = null;

    if (diff < -50) {
      nextSlide();
    } else if (diff > 50) {
      prevSlide();
    }
  };

  if (!items?.length) return null;

  const renderSlideContent = (item: ImageCarouselItem, idx: number) => {
    const isEager = hasMultiple ? idx === 1 : idx === 0;
    const img = (
      <img
        src={item.src}
        alt={item.alt}
        style={styles.image}
        loading={isEager ? 'eager' : 'lazy'}
        draggable={false}
      />
    );

    const handleClick = (e: React.MouseEvent) => {
      if (hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (!item.url) {
      return <div className="w-100 h-100">{img}</div>;
    }

    if (isExternal(item.url)) {
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="d-block w-100 h-100"
          onClick={handleClick}
        >
          {img}
        </a>
      );
    }

    return (
      <Link to={item.url} className="d-block w-100 h-100" onClick={handleClick}>
        {img}
      </Link>
    );
  };

  // Transform calculation: combines slide index and active drag pixel offset
  const transformStyle = hasMultiple
    ? dragOffset !== 0
      ? `translate3d(calc(-${displayIndex * 100}% + ${dragOffset}px), 0, 0)`
      : `translate3d(-${displayIndex * 100}%, 0, 0)`
    : 'translate3d(0, 0, 0)';

  const transitionStyle = isDragging
    ? 'none'
    : isTransitioning
      ? 'transform 700ms cubic-bezier(0.25, 1, 0.5, 1)'
      : 'none';

  const arrowClass =
    'btn btn-light bg-white border-0 shadow-sm rounded-3 p-0 position-absolute top-50 translate-middle-y z-2 d-none d-md-flex align-items-center justify-content-center';

  return (
    <div
      className={`position-relative rounded-4 overflow-hidden shadow-sm ${className}`}
      style={{ ...styles.container(aspectRatio), ...style }}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => {
        if (pauseOnHover) setIsPaused(false);
        if (isDragging) handleMouseUp();
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Horizontal scrolling track */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          transform: transformStyle,
          transition: transitionStyle,
          willChange: 'transform',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {slides.map((item, i) => (
          <div key={i} style={styles.slide}>
            {renderSlideContent(item, i)}
          </div>
        ))}
      </div>

      {hasMultiple && (
        <>
          {showArrows && (
            <>
              <button
                type="button"
                aria-label="Previous slide"
                className={`${arrowClass} start-0 ms-3`}
                style={styles.arrow}
                onClick={prevSlide}
              >
                <FaChevronLeft size={12} />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                className={`${arrowClass} end-0 me-3`}
                style={styles.arrow}
                onClick={nextSlide}
              >
                <FaChevronRight size={12} />
              </button>
            </>
          )}

          {showIndicators && (
            <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 d-flex gap-2 z-2">
              {items.map((_, i) => {
                const active = i === activeDot;
                const dotColorClass =
                  indicatorVariant === 'primary'
                    ? active
                      ? 'bg-primary'
                      : 'bg-white bg-opacity-75'
                    : active
                      ? 'bg-white'
                      : 'bg-white bg-opacity-50';

                return (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    aria-current={active}
                    className={`border-0 p-0 ${dotColorClass}`}
                    style={styles.dot(active)}
                    onClick={() => goToSlide(i)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageCarousel;
